import { materials, phases, projects, type MaterialId } from '../config/content';
import type { SceneManager } from '../core/SceneManager';
import { ScrollController } from '../core/ScrollController';
import { InteriorTour } from '../interior/InteriorTour';
import { Router } from './Router';
import { pageTemplate, shellTemplate } from './templates';

export class App {
  private scene?: SceneManager;
  private tourUI?: InteriorTour;
  private scroll!: ScrollController;
  private router!: Router;
  private pageRoot!: HTMLElement;
  private progress = 0;
  private currentPath = '';
  private selectedMaterial: MaterialId = 'concrete';
  constructor(
    private readonly root: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
  ) {}

  async init(): Promise<void> {
    this.root.innerHTML = shellTemplate();
    this.pageRoot = this.root.querySelector<HTMLElement>('main')!;
    this.scroll = new ScrollController((progress) => this.updateProgress(progress));
    this.router = new Router((url, push) => this.navigate(url, push));
    this.router.start();
    this.bindShell();
    this.render(location.pathname);
    this.restoreHash(location.hash);
    this.headerState();
    try {
      const { SceneManager } = await import('../core/SceneManager');
      this.scene = new SceneManager(this.canvas);
      await this.scene.init();
      this.tourUI = new InteriorTour(this.scene, this.scroll);
      document.documentElement.classList.add('renderer-ready');
      this.mountScene();
    } catch (error) {
      console.warn('The interactive model is unavailable; architectural imagery remains available.', error);
      document.documentElement.classList.add('renderer-failed');
      document
        .querySelectorAll<HTMLButtonElement>('[data-explode], [data-light], [data-view], [data-enter-tour]')
        .forEach((button) => (button.disabled = true));
    }
  }
  private render(path: string): void {
    this.tourUI?.close();
    this.currentPath = path;
    this.pageRoot.innerHTML = pageTemplate(path);
    document.body.dataset.page =
      path === '/' ? 'home' : path.startsWith('/projects/') ? 'project-detail' : path.slice(1);
    const project = projects.find((p) => path === '/projects/' + p.slug);
    const names: Record<string, string> = {
      '/': 'Good buildings. Built properly.',
      '/projects': 'Project concepts',
      '/capabilities': 'Capabilities',
      '/process': 'Our process',
      '/about': 'About',
      '/contact': 'Let’s talk',
    };
    document.title = (project?.name ?? names[path] ?? 'Page not found') + ' — Built by Keemo';
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute('href', 'https://portfolioconcept01.builtbykeemo.com' + path);
    document.querySelectorAll<HTMLAnchorElement>('.main-nav a').forEach((a) => {
      if (path === a.pathname || path.startsWith(a.pathname + '/')) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
    this.selectedMaterial = 'concrete';
    this.bindPage();
    this.scroll.mount();
    this.mountScene();
  }
  private navigate(url: string, push: boolean): void {
    const next = new URL(url, location.origin);
    this.setMenu(false);
    if (push && next.pathname + next.hash !== location.pathname + location.hash)
      history.pushState({}, '', next.pathname + next.hash);
    if (next.pathname !== this.currentPath) this.render(next.pathname);
    this.scroll.scrollTo(0, true);
    this.restoreHash(next.hash);
    this.headerState();
    this.pageRoot.focus({ preventScroll: true });
  }
  private restoreHash(hash: string): void {
    if (!hash) return;
    requestAnimationFrame(() => {
      const element = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (element) this.scroll.scrollTo(element, true);
    });
  }
  private mountScene(): void {
    this.scene?.mount(
      document.querySelector('[data-scene-host]'),
      document.querySelector('[data-material-host]'),
    );
    this.scene?.setProgress(this.progress);
    this.updateProgress(this.progress);
    this.scene?.setMaterial(this.selectedMaterial);
  }
  private updateProgress(progress: number): void {
    this.progress = progress;
    this.scene?.setProgress(progress);
    const enter = this.pageRoot.querySelector<HTMLButtonElement>('[data-enter-tour]');
    if (enter) {
      enter.disabled = progress < 0.985 || !document.documentElement.classList.contains('renderer-ready');
      enter.textContent =
        progress < 0.985 ? 'Interior tour · available when built' : 'Explore the interior ↗';
    }
    const index = Math.min(5, Math.round(progress * 5));
    const p = phases[index];
    this.pageRoot.querySelector('#construction-panel')?.setAttribute('aria-labelledby', 'phase-tab-' + index);
    const fields = { number: p.number, title: p.headline, body: p.body, detail: p.detail };
    Object.entries(fields).forEach(([key, value]) => {
      const element = this.pageRoot.querySelector('[data-phase-' + key + ']');
      if (element && element.textContent !== value) element.textContent = value;
    });
    this.pageRoot.querySelectorAll<HTMLButtonElement>('[data-phase]').forEach((b, i) => {
      b.setAttribute('aria-selected', String(i === index));
      b.tabIndex = i === index ? 0 : -1;
    });
  }
  private bindShell(): void {
    document
      .querySelector('.menu-button')
      ?.addEventListener('click', () => this.setMenu(!document.body.classList.contains('menu-open')));
    document.addEventListener('keydown', this.onKeyDown);
    addEventListener('scroll', this.headerState, { passive: true });
  }
  private readonly headerState = (): void => {
    document.body.classList.toggle('has-scrolled', scrollY > 50);
  };
  private setMenu(open: boolean): void {
    const wasOpen = document.body.classList.contains('menu-open');
    const panel = document.querySelector<HTMLElement>('.menu-panel')!;
    const button = document.querySelector<HTMLButtonElement>('.menu-button')!;
    document.body.classList.toggle('menu-open', open);
    button.setAttribute('aria-expanded', String(open));
    button.innerHTML = open
      ? 'Close <span aria-hidden="true">×</span>'
      : 'Menu <span aria-hidden="true">+</span>';
    panel.hidden = !open;
    panel.inert = !open;
    this.pageRoot.inert = open;
    if (open) {
      this.scroll.stop();
      panel.querySelector('a')?.focus();
    } else {
      this.scroll.start();
      if (wasOpen) button.focus();
    }
  }
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!document.body.classList.contains('menu-open')) return;
    if (event.key === 'Escape') {
      this.setMenu(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [
      document.querySelector<HTMLButtonElement>('.menu-button')!,
      ...document.querySelectorAll<HTMLAnchorElement>('.menu-panel a'),
    ];
    const index = focusable.indexOf(document.activeElement as HTMLButtonElement);
    if (event.shiftKey && index <= 0) {
      event.preventDefault();
      focusable.at(-1)?.focus();
    } else if (!event.shiftKey && index === focusable.length - 1) {
      event.preventDefault();
      focusable[0].focus();
    }
  };
  private bindPage(): void {
    this.pageRoot.querySelector('[data-enter-tour]')?.addEventListener('click', () => this.tourUI?.enter());
    const phaseButtons = [...this.pageRoot.querySelectorAll<HTMLButtonElement>('[data-phase]')];
    phaseButtons.forEach((b, index) => b.addEventListener('click', () => this.scroll.selectPhase(index)));
    this.tabKeys(phaseButtons);
    this.pageRoot.querySelector('[data-replay]')?.addEventListener('click', () => {
      this.scene?.setExploded(false);
      const button = this.pageRoot.querySelector('[data-explode]');
      button?.setAttribute('aria-pressed', 'false');
      if (button) button.innerHTML = 'Explode layers <span aria-hidden="true">↕</span>';
      this.scene?.setView('front');
      this.pageRoot
        .querySelectorAll<HTMLButtonElement>('[data-view]')
        .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === 'front')));
      this.scroll.selectPhase(0);
    });
    this.pageRoot.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) =>
      button.addEventListener('click', () => {
        this.pageRoot
          .querySelectorAll<HTMLButtonElement>('[data-view]')
          .forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        this.scene?.setView(button.dataset.view as 'front' | 'rear');
      }),
    );
    this.pageRoot.querySelector<HTMLButtonElement>('[data-explode]')?.addEventListener('click', (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const exploded = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(exploded));
      button.innerHTML =
        (exploded ? 'Assemble layers' : 'Explode layers') + ' <span aria-hidden="true">↕</span>';
      this.scene?.setExploded(exploded);
    });
    this.pageRoot.querySelector<HTMLButtonElement>('[data-light]')?.addEventListener('click', (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const evening = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(evening));
      button.innerHTML = (evening ? 'Daylight' : 'Evening light') + ' <span aria-hidden="true">◐</span>';
      this.scene?.setEvening(evening);
    });
    const materialButtons = [...this.pageRoot.querySelectorAll<HTMLButtonElement>('[data-material]')];
    materialButtons.forEach((button) =>
      button.addEventListener('click', () => {
        const material = materials.find((m) => m.id === button.dataset.material)!;
        this.selectedMaterial = material.id;
        this.scene?.setMaterial(material.id);
        this.pageRoot
          .querySelector('#material-panel')
          ?.setAttribute('aria-labelledby', 'material-tab-' + material.id);
        materialButtons.forEach((b) => {
          b.setAttribute('aria-selected', String(b === button));
          b.tabIndex = b === button ? 0 : -1;
        });
        for (const key of ['spec', 'detail', 'use'] as const) {
          const field = this.pageRoot.querySelector('[data-material-' + key + ']');
          if (field) field.textContent = material[key];
        }
        this.pageRoot.querySelector<HTMLElement>('[data-sample]')!.dataset.sample = material.id;
      }),
    );
    this.tabKeys(materialButtons);
    this.pageRoot.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach((button) =>
      button.addEventListener('click', () => {
        this.pageRoot
          .querySelectorAll('[data-filter]')
          .forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        this.pageRoot.querySelectorAll<HTMLElement>('[data-category]').forEach((card) => {
          card.hidden = button.dataset.filter !== 'all' && button.dataset.filter !== card.dataset.category;
        });
      }),
    );
    this.pageRoot.querySelector<HTMLFormElement>('.contact-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      if (!form.reportValidity()) return;
      const data = new FormData(form);
      const preview = form.querySelector<HTMLElement>('.inquiry-preview')!;
      preview.hidden = false;
      preview.querySelector('[data-inquiry]')!.textContent =
        'Project: ' +
        String(data.get('interest')) +
        '\nName: ' +
        String(data.get('name')) +
        '\nEmail: ' +
        String(data.get('email')) +
        '\n\n' +
        String(data.get('message'));
      preview.querySelector('.form-status')!.textContent = 'Preview only — no inquiry has been sent.';
      preview.focus({ preventScroll: true });
      this.scroll.scrollTo(preview);
    });
    this.pageRoot.querySelector('[data-edit-inquiry]')?.addEventListener('click', () => {
      const preview = this.pageRoot.querySelector<HTMLElement>('.inquiry-preview')!;
      preview.hidden = true;
      const firstField = this.pageRoot.querySelector<HTMLInputElement>('#name')!;
      firstField.focus({ preventScroll: true });
      this.scroll.scrollTo(firstField);
    });
  }
  private tabKeys(buttons: HTMLButtonElement[]): void {
    buttons.forEach((button, i) =>
      button.addEventListener('keydown', (event) => {
        let next: number | undefined;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % buttons.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
          next = (i + buttons.length - 1) % buttons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = buttons.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          buttons[next].click();
          buttons[next].focus();
        }
      }),
    );
  }
  destroy(): void {
    this.router.destroy();
    this.scroll.destroy();
    this.tourUI?.destroy();
    this.scene?.destroy();
    removeEventListener('scroll', this.headerState);
    document.removeEventListener('keydown', this.onKeyDown);
  }
}
